import type { OpenAPI } from "simplydo/interfaces";
import type {
  UserMe,
  UserMeOwnerOrganisation,
} from "simplydo/interfaces/types/userTypes";

import type { UserAction } from "../reducers/user";

const userActions = {
  RECEIVE_USER: "RECEIVE_USER",
  CREATE_GROUP: "CREATE_GROUP",
  JOIN_GROUP: "JOIN_GROUP",
  LEAVE_GROUP: "LEAVE_GROUP",
  UPDATE_TERMS_AGREEMENT: "UPDATE_TERMS_AGREEMENT",
  UPDATE_PROFILE: "UPDATE_PROFILE",
  UPDATE_EMAILS: "UPDATE_EMAILS",
  UPDATE_PHONE_NUMBERS: "UPDATE_PHONE_NUMBERS",
  TOGGLE_EVENT_SUBSCRIPTION: "TOGGLE_EVENT_SUBSCRIPTION",
  SWITCH_ORGANISATION: "SWITCH_ORGANISATION",
  UPDATE_ORGANISATION: "UPDATE_ORGANISATION",
  ADD_ENGAGEMENT: "ADD_ENGAGEMENT",
  LOGOUT: "LOGOUT",
  UPDATE_PERMISSIONS: "UPDATE_PERMISSIONS",
  UPDATE_PUBLIC_IDEAS: "UPDATE_PUBLIC_IDEAS",
  UPDATE_ENABLED_SERVICES: "UPDATE_ENABLED_SERVICES",
  UPDATE_EMAIL_ADDRESS: "UPDATE_EMAIL_ADDRESS",
  GHOST: "GHOST",
  UNGHOST: "UNGHOST",
  SHOW_DASHBOARD_TEXT: "SHOW_DASHBOARD_TEXT",
  HIDE_DASHBOARD_TEXT: "HIDE_DASHBOARD_TEXT",
  HIDE_SECURITY_RECOMMENDATIONS: "HIDE_SECURITY_RECOMMENDATIONS",
  UPDATE_FEATURE_FLAGS: "UPDATE_FEATURE_FLAGS",
  UPDATE_TABLE_PREFS: "UPDATE_TABLE_PREFS",
  ON_FOLLOW: "ON_FOLLOW",
  ADD_IDEA_ID: "ADD_IDEA_ID",
  ADD_COMPANY: "ADD_COMPANY",
  UPDATE_TAGS: "UPDATE_TAGS",
  TRACK_ONBOARDING_COMPLETION: "TRACK_ONBOARDING_COMPLETION",

  receiveUser(user: Partial<UserMe>): UserAction {
    return { type: "RECEIVE_USER", user };
  },
  createGroup(id: string, name: string): UserAction {
    return { type: "CREATE_GROUP", id, name };
  },
  joinGroup(group: { _id: string; name: string }): UserAction {
    return { type: "JOIN_GROUP", id: group._id, name: group.name };
  },
  leaveGroup(group: { _id: string }): UserAction {
    return { type: "LEAVE_GROUP", id: group._id };
  },
  agreeTerms(legalAgreements: UserMe["legalAgreements"]): UserAction {
    return { type: "UPDATE_TERMS_AGREEMENT", legalAgreements };
  },
  updateProfile(profile: Partial<NonNullable<UserMe["profile"]>>): UserAction {
    return { type: "UPDATE_PROFILE", profile };
  },
  updatePhoneNumbers(phoneNumbers: UserMe["phoneNumbers"]): UserAction {
    return { type: "UPDATE_PHONE_NUMBERS", phoneNumbers };
  },
  updateEmails(emails: UserMe["emails"]): UserAction {
    return { type: "UPDATE_EMAILS", emails };
  },
  toggleEventSubscription(
    subscriptions: UserMe["eventSubscriptions"],
  ): UserAction {
    return { type: "TOGGLE_EVENT_SUBSCRIPTION", subscriptions };
  },
  switchOrganisation(organisation: UserMeOwnerOrganisation): UserAction {
    return { type: "SWITCH_ORGANISATION", organisation };
  },
  updateOrganisation(field: string, value: unknown): UserAction {
    return { type: "UPDATE_ORGANISATION", field, value };
  },
  logout(): UserAction {
    return { type: "LOGOUT" };
  },
  addEngagement(engagement: string): UserAction {
    return { type: "ADD_ENGAGEMENT", engagement };
  },
  updatePermissions(scope: string, permissions: string[]): UserAction {
    return { type: "UPDATE_PERMISSIONS", scope, permissions };
  },
  updatePublicIdeas(): UserAction {
    return { type: "UPDATE_PUBLIC_IDEAS" };
  },
  updateEnabledServices(services: UserMe["enabledServices"]): UserAction {
    return { type: "UPDATE_ENABLED_SERVICES", services };
  },
  updateEmailAddress(email: string): UserAction {
    return { type: "UPDATE_EMAIL_ADDRESS", email };
  },
  updateFeatureFlags(featureFlags: UserMe["featureFlags"]): UserAction {
    return { type: "UPDATE_FEATURE_FLAGS", featureFlags };
  },
  updateTablePreferences(key: string, preferences: unknown): UserAction {
    return { type: "UPDATE_TABLE_PREFS", key, preferences };
  },
  ghost(id: string): UserAction {
    return { type: "GHOST", id };
  },
  unGhost(): UserAction {
    return { type: "UNGHOST" };
  },
  hideDashboardText(): UserAction {
    return { type: "HIDE_DASHBOARD_TEXT" };
  },
  showDashboardText(): UserAction {
    return { type: "SHOW_DASHBOARD_TEXT" };
  },
  hideSecurityRecommendations(): UserAction {
    return { type: "HIDE_SECURITY_RECOMMENDATIONS" };
  },
  updateTags(tags: OpenAPI.GET<"/tags">["response"]): UserAction {
    return { type: "UPDATE_TAGS", tags };
  },
  onFollow(context: string, id: string, manual = false): UserAction {
    return { type: "ON_FOLLOW", context, id, manual };
  },
  addIdeaId(ideaId: string): UserAction {
    return { type: "ADD_IDEA_ID", ideaId };
  },
  addCompany(company: NonNullable<UserMe["companies"]>[number]): UserAction {
    return { type: "ADD_COMPANY", company };
  },
  trackOnboardingCompletion(): UserAction {
    return { type: "TRACK_ONBOARDING_COMPLETION" };
  },
};

export default userActions;
